/**
 * iter132c — Payment link share dialog for admin.
 *
 * When admin taps "Send Payment Link" we:
 *   1. Generate a fresh Stripe Checkout URL server-side (POST /admin/pending-payments/{id}/send-link)
 *   2. Immediately copy the URL to the clipboard
 *   3. Open THIS dialog with 5 quick-share actions:
 *        📱 Send by Text Message  → sms: link
 *        💬 Share (native)        → navigator.share() when available (iOS/Android)
 *        📋 Copy Link             → re-copies to clipboard
 *        📧 Send by Email         → mailto: link (works whether SMTP is up or not)
 *        🔗 Open Payment Page     → opens checkout URL in new tab
 *
 * The backup transport (auto-email + auto-SMS via Twilio) is still
 * queued server-side, but the admin never has to wait for those — the
 * URL is on the clipboard the moment the dialog opens.
 */
import { useEffect, useState } from "react";
import { Button } from "./ui/button";
import { MessageSquare, Mail, Copy, ExternalLink, Share2, CheckCircle2, X } from "lucide-react";
import { toast } from "sonner";

export default function PaymentLinkShareDialog({ open, onClose, data }) {
  const [copiedRecently, setCopiedRecently] = useState(false);
  const [nativeShareSupported, setNativeShareSupported] = useState(false);

  useEffect(() => {
    if (typeof navigator !== "undefined" && typeof navigator.share === "function") {
      setNativeShareSupported(true);
    }
  }, []);

  if (!open || !data) return null;

  const { checkout_url, amount, customer_name, customer_email, customer_phone } = data;
  const firstName = (customer_name || "").split(" ")[0] || "there";
  const amountStr = typeof amount === "number" ? `$${amount.toFixed(2)}` : "";
  const smsBody = `Hi ${firstName}! Please secure your JDSN Lawn Care booking by paying ${amountStr} here: ${checkout_url}`;
  const emailSubject = "Your JDSN Lawn Care payment link";
  const emailBody =
    `Hi ${firstName},\n\n` +
    `Please secure your JDSN Lawn Care booking by paying ${amountStr} through this secure link:\n\n` +
    `${checkout_url}\n\n` +
    `Thanks,\nJDSN Lawn Care`;

  const doCopy = async () => {
    try {
      await navigator.clipboard.writeText(checkout_url);
      setCopiedRecently(true);
      toast.success("Payment link copied to clipboard");
      setTimeout(() => setCopiedRecently(false), 2200);
    } catch {
      // Fallback for browsers that block clipboard: select + prompt
      window.prompt("Copy this payment link:", checkout_url);
    }
  };

  const doShare = async () => {
    if (!nativeShareSupported) return doCopy();
    try {
      await navigator.share({
        title: "JDSN Lawn Care — Payment link",
        text: smsBody,
        url: checkout_url,
      });
    } catch (e) {
      // Ignore AbortError (user cancelled share sheet)
      if (e?.name !== "AbortError") toast.error("Share failed");
    }
  };

  const doOpen = () => {
    window.open(checkout_url, "_blank", "noopener,noreferrer");
  };

  const smsHref = customer_phone
    ? `sms:${customer_phone}?&body=${encodeURIComponent(smsBody)}`
    : null;
  const emailHref = customer_email
    ? `mailto:${customer_email}?subject=${encodeURIComponent(emailSubject)}&body=${encodeURIComponent(emailBody)}`
    : null;

  return (
    <div
      className="fixed inset-0 z-[70] bg-black/60 flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="payment-link-share-dialog"
    >
      <div
        className="bg-white w-full max-w-md rounded-2xl shadow-2xl overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <CheckCircle2 className="w-5 h-5 text-[#047857]" />
            <div className="font-extrabold tracking-tight font-[Outfit]">Payment link copied</div>
          </div>
          <Button variant="ghost" size="sm" onClick={onClose} aria-label="Close"
            data-testid="payment-link-share-close-btn"><X className="w-4 h-4" /></Button>
        </div>

        <div className="px-5 py-4 space-y-3">
          <div className="text-sm text-gray-700 leading-relaxed">
            The link is on your clipboard — share it with{" "}
            <span className="font-bold">{customer_name || "your customer"}</span>{" "}
            for <span className="font-bold text-[#047857]">{amountStr}</span> however you like.
          </div>

          {/* URL preview + inline copy */}
          <div
            className="flex items-center gap-2 rounded-xl bg-gray-50 border border-gray-200 px-3 py-2 text-xs font-mono text-gray-700 break-all cursor-pointer hover:bg-gray-100"
            onClick={doCopy}
            data-testid="payment-link-preview"
            title="Click to copy"
          >
            <div className="flex-1 min-w-0 truncate">{checkout_url}</div>
            {copiedRecently
              ? <CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0" />
              : <Copy className="w-4 h-4 text-gray-500 shrink-0" />}
          </div>

          <div className="grid grid-cols-2 gap-2">
            {smsHref ? (
              <a
                href={smsHref}
                className="h-11 rounded-full bg-[#047857] hover:bg-[#065F46] text-white font-extrabold text-sm inline-flex items-center justify-center gap-1.5 shadow-lg shadow-emerald-900/20"
                data-testid="payment-link-sms-btn"
              >
                <MessageSquare className="w-4 h-4" /> Text message
              </a>
            ) : (
              <div className="h-11 rounded-full bg-gray-100 text-gray-400 font-bold text-sm inline-flex items-center justify-center gap-1.5" title="No phone on file">
                <MessageSquare className="w-4 h-4" /> Text (no phone)
              </div>
            )}
            <Button
              onClick={doShare}
              disabled={!nativeShareSupported}
              className="h-11 rounded-full bg-white text-[#047857] border border-emerald-200 hover:bg-emerald-50 font-extrabold text-sm disabled:opacity-40"
              data-testid="payment-link-share-btn"
              title={nativeShareSupported ? "Use your device's share sheet" : "Native share not available on this browser"}
            >
              <Share2 className="w-4 h-4 mr-1.5" /> Share
            </Button>
            <Button
              onClick={doCopy}
              className="h-11 rounded-full bg-white text-[#047857] border border-emerald-200 hover:bg-emerald-50 font-extrabold text-sm"
              data-testid="payment-link-copy-btn"
            >
              <Copy className="w-4 h-4 mr-1.5" /> Copy link
            </Button>
            {emailHref ? (
              <a
                href={emailHref}
                className="h-11 rounded-full bg-white text-[#047857] border border-emerald-200 hover:bg-emerald-50 font-extrabold text-sm inline-flex items-center justify-center gap-1.5"
                data-testid="payment-link-email-btn"
              >
                <Mail className="w-4 h-4" /> Send email
              </a>
            ) : (
              <div className="h-11 rounded-full bg-gray-100 text-gray-400 font-bold text-sm inline-flex items-center justify-center gap-1.5" title="No email on file">
                <Mail className="w-4 h-4" /> Email (no email)
              </div>
            )}
            <Button
              onClick={doOpen}
              className="col-span-2 h-11 rounded-full bg-[#047857]/10 text-[#047857] border border-emerald-200 hover:bg-[#047857]/15 font-extrabold text-sm"
              data-testid="payment-link-open-btn"
            >
              <ExternalLink className="w-4 h-4 mr-1.5" /> Open payment page
            </Button>
          </div>

          <div className="text-[11px] text-gray-500 leading-snug pt-1">
            Backup delivery: we also queued this link to be sent via SMS
            {customer_email ? " and email " : " "}
            automatically — but the link on your clipboard is always the fastest way to share.
          </div>
        </div>
      </div>
    </div>
  );
}
